import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Check, Sparkles, ShieldCheck, Smartphone, Zap, Globe2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { useLang } from "../contexts/LanguageContext";
import { useAuth } from "../contexts/AuthContext";
import { api } from "../lib/api";
import { toast } from "sonner";
import useLocalizedSEO from "../hooks/useLocalizedSEO";

const COPY = {
  badge: { de: "15 Tage kostenlos · Keine Kreditkarte", en: "15-day free trial · No credit card", tr: "15 gün ücretsiz · Kredi kartı yok" },
  headline: { de: "Digitale QR-Menüs in 5 Minuten erstellen", en: "Create digital QR menus in 5 minutes", tr: "5 dakikada dijital QR menüler oluşturun" },
  subheadline: { de: "Modern, mehrsprachig und perfekt für Restaurants in Deutschland.", en: "Modern, multilingual and perfect for restaurants in Germany.", tr: "Modern, çok dilli ve Almanya'daki restoranlar için ideal." },
  popular: { de: "Am beliebtesten", en: "Most popular", tr: "En popüler" },
  perMonth: { de: "/ Monat", en: "/ month", tr: "/ ay" },
  perYear: { de: "/ Jahr", en: "/ year", tr: "/ yıl" },
  monthly: { de: "Monatlich", en: "Monthly", tr: "Aylık" },
  yearly: { de: "Jährlich", en: "Yearly", tr: "Yıllık" },
  save: { de: "−17 %", en: "−17%", tr: "−%17" },
  monthEquiv: { de: "entspricht", en: "equals", tr: "aylık" },
  trustDsgvo: { de: "DSGVO-konform", en: "GDPR compliant", tr: "GDPR uyumlu" },
  trustNoApp: { de: "Keine App nötig", en: "No app needed", tr: "Uygulama gerekmez" },
  trustInstant: { de: "Sofort einsatzbereit", en: "Ready instantly", tr: "Anında hazır" },
  trustMulti: { de: "Mehrsprachig", en: "Multilingual", tr: "Çok dilli" },
  footnote: { de: "Abonnement verlängert sich automatisch · Jederzeit kündbar.", en: "Auto-renews · Cancel anytime.", tr: "Otomatik yenilenir · İstediğiniz zaman iptal." },
};

export default function Pricing() {
  const { lang } = useLang();
  const { user } = useAuth();
  const nav = useNavigate();
  const [plans, setPlans] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [yearly, setYearly] = useState(false);

  useLocalizedSEO({
    titleByLang: {
      de: "Preise · Digitale Speisekarte & QR Menü für Restaurants",
      en: "Pricing · QR Menu for Restaurants · Restaurant Menu SaaS",
      tr: "Fiyatlar · Restoranlar için QR Menü Sistemi · Dijital Menü",
    },
    descByLang: {
      de: "Preise für qrmenu-pro.com — digitale Speisekarte und QR Menü für Restaurants. 15 Tage kostenlos testen.",
      en: "Pricing for qrmenu-pro.com — QR Menu for Restaurants and Restaurant Menu SaaS. 15-day free trial.",
      tr: "qrmenu-pro.com fiyatları — Restoranlar için QR Menü Sistemi ve Dijital Restoran Menüsü. 15 gün ücretsiz dene.",
    },
    keywordsByLang: {
      de: "QR Menü Preise, Digitale Speisekarte Kosten, Restaurant Menü System Preis, QR Code Menükarte",
      en: "QR menu pricing, digital restaurant menu cost, restaurant menu SaaS pricing, contactless menu",
      tr: "QR menü fiyatı, dijital restoran menüsü ücreti, QR kod menü maliyeti, çok dilli menü",
    },
  });

  useEffect(() => { api.get("/plans").then((r) => setPlans(r.data)); }, []);

  const tx = (k) => COPY[k]?.[lang] || COPY[k]?.de || k;

  const choose = async (plan, provider = "paypal") => {
    if (!user) {
      // Save the user's intent so we can resume checkout after login
      try {
        localStorage.setItem("qrmp_pending_checkout", JSON.stringify({
          plan_id: plan.id, provider, yearly: !!yearly, ts: Date.now(),
        }));
      } catch (e) {}
      nav("/register");
      return;
    }
    setBusyId(`${plan.id}-${provider}`);
    try {
      if (provider === "stripe") {
        const packageId = `${plan.id}_${yearly ? "yearly" : "monthly"}`;
        const { data } = await api.post("/payments/stripe/checkout", {
          package_id: packageId,
          origin_url: window.location.origin,
        });
        if (data.url) window.location.href = data.url;
      } else {
        const { data } = await api.post("/payments/checkout", {
          plan_id: plan.id,
          billing_period: yearly ? "yearly" : "monthly",
          origin_url: window.location.origin,
        });
        if (data.url) window.location.href = data.url;
      }
    } catch (e) {
      const code = e.response?.status;
      const detail = e.response?.data?.detail;
      const isExpired = e.response?.isAuthExpired || code === 401;
      let msg;
      if (isExpired) {
        msg = lang === "de"
          ? "Deine Sitzung ist abgelaufen. Bitte logge dich erneut ein."
          : lang === "tr"
          ? "Oturumunuz sona erdi. Lütfen tekrar giriş yapın."
          : "Your session has expired. Please log in again.";
        try {
          localStorage.setItem("qrmp_pending_checkout", JSON.stringify({
            plan_id: plan.id, provider, yearly: !!yearly, ts: Date.now(),
          }));
        } catch (err) {}
        toast.error(msg, { duration: 6000 });
        setTimeout(() => nav("/login"), 1200);
        return;
      }
      if (code === 404) {
        msg = provider === "stripe"
          ? (lang === "de" ? "Stripe-Endpoint nicht gefunden — bitte App erneut deployen."
            : lang === "tr" ? "Stripe endpoint bulunamadı — uygulamayı tekrar deploy etmen gerekiyor."
            : "Stripe endpoint not found — please redeploy the app.")
          : "Endpoint not found";
      } else if (code === 500 && /not configured|nicht konfiguriert|yapılandırılmamış/i.test(detail || "")) {
        msg = lang === "de"
          ? (detail || "Stripe ist auf dem Server nicht konfiguriert.")
          : lang === "tr"
          ? (detail || "Stripe sunucuda yapılandırılmamış.")
          : (detail || "Stripe is not configured on the server.");
      } else if (code === 502) {
        msg = lang === "de"
          ? `Zahlungsanbieter-Fehler: ${detail || "Bitte erneut versuchen."}`
          : lang === "tr"
          ? `Ödeme sağlayıcı hatası: ${detail || "Lütfen tekrar deneyin."}`
          : `Payment provider error: ${detail || "Please try again."}`;
      } else {
        msg = detail || e.message || (lang === "de" ? "Zahlungsfehler"
                                    : lang === "tr" ? "Ödeme hatası"
                                    : "Payment error");
      }
      toast.error(msg, { duration: 8000 });
      console.error("Payment error:", { code, detail, raw: e });
    } finally {
      setBusyId(null);
    }
  };

  // Resume an interrupted checkout: if the user was sent away (login/register)
  // mid-flow, replay the choice once they come back authenticated.
  useEffect(() => {
    if (!user) return;
    let pending;
    try { pending = JSON.parse(localStorage.getItem("qrmp_pending_checkout") || "null"); } catch (e) {}
    if (!pending) return;
    // Only honour very recent intents (< 30 min) to avoid surprise auto-checkout
    if (Date.now() - (pending.ts || 0) > 30 * 60 * 1000) {
      localStorage.removeItem("qrmp_pending_checkout");
      return;
    }
    localStorage.removeItem("qrmp_pending_checkout");
    // Defer until plans list is available
    if (plans.length === 0) return;
    const plan = plans.find((p) => p.id === pending.plan_id);
    if (!plan) return;
    setYearly(!!pending.yearly);
    // small delay so toast/UI settles
    setTimeout(() => choose(plan, pending.provider || "paypal"), 600);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, plans]);

  const trustItems = [
    { icon: ShieldCheck, label: tx("trustDsgvo") },
    { icon: Smartphone, label: tx("trustNoApp") },
    { icon: Zap, label: tx("trustInstant") },
    { icon: Globe2, label: tx("trustMulti") },
  ];

  return (
    <div
      className="min-h-screen bg-zinc-950 text-zinc-100 relative overflow-hidden"
      data-testid="pricing-page"
      style={{ marginTop: "-4rem", paddingTop: "4rem" }}
    >
      {/* Premium dark background with neon glows */}
      <div aria-hidden className="pointer-events-none absolute inset-0 overflow-hidden">
        <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-purple-600/20 blur-[120px]" />
        <div className="absolute top-1/2 -right-40 w-[600px] h-[600px] rounded-full bg-fuchsia-600/15 blur-[110px]" />
        <div className="absolute bottom-0 -left-40 w-[500px] h-[500px] rounded-full bg-violet-600/10 blur-[100px]" />
        {/* Subtle grid */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(255,255,255,1) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,1) 1px, transparent 1px)",
            backgroundSize: "60px 60px",
          }}
        />
      </div>

      <div className="relative container mx-auto px-5 lg:px-8 py-20 lg:py-28 max-w-5xl">
        {/* Hero */}
        <div className="text-center max-w-3xl mx-auto">
          <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold bg-purple-500/10 text-purple-300 border border-purple-500/30 backdrop-blur" data-testid="pricing-badge">
            <Sparkles className="h-3.5 w-3.5" />
            {tx("badge")}
          </span>
          <h1 className="mt-7 text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
            {tx("headline")}
          </h1>
          <p className="mt-5 text-base lg:text-lg text-zinc-400 leading-relaxed max-w-2xl mx-auto">
            {tx("subheadline")}
          </p>

          {/* Trust badges */}
          <div className="mt-10 flex flex-wrap justify-center gap-x-7 gap-y-3 text-sm text-zinc-400" data-testid="trust-row">
            {trustItems.map((it, i) => {
              const Icon = it.icon;
              return (
                <div key={i} className="inline-flex items-center gap-1.5">
                  <Icon className="h-4 w-4 text-purple-400" />
                  <span className="font-medium">{it.label}</span>
                </div>
              );
            })}
          </div>
        </div>

        {/* Billing toggle */}
        <div className="mt-14 flex items-center justify-center gap-4">
          <span className={`text-sm font-semibold transition-colors ${!yearly ? "text-white" : "text-zinc-500"}`}>
            {tx("monthly")}
          </span>
          <button
            onClick={() => setYearly(!yearly)}
            className="relative w-14 h-7 rounded-full bg-zinc-800 border border-zinc-700 transition-colors hover:bg-zinc-700"
            data-testid="billing-toggle"
            aria-label="Toggle billing"
          >
            <div className={`absolute top-0.5 w-5 h-5 rounded-full bg-purple-500 shadow-lg shadow-purple-500/50 transition-transform ${yearly ? "translate-x-7" : "translate-x-0.5"}`} />
          </button>
          <span className={`text-sm font-semibold transition-colors flex items-center gap-2 ${yearly ? "text-white" : "text-zinc-500"}`}>
            {tx("yearly")}
            <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 text-[10px] font-bold border border-emerald-500/30">
              {tx("save")}
            </span>
          </span>
        </div>

        {/* Cards — 3 plans, centered */}
        <div className="mt-10 lg:mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 max-w-6xl mx-auto items-stretch">
          {plans.map((p) => {
            const isPro = p.id === "pro";
            const isPremium = p.id === "premium";
            const isFeatured = isPro; // "most popular" band remains Pro
            const features = p.features?.[lang] || p.features?.de || [];
            const subtitle = p.subtitle?.[lang] || p.subtitle?.de || "";
            const monthlyDisplay = yearly ? Math.round(p.price_yearly / 12) : p.price_monthly;
            const totalYearly = p.price_yearly;

            return (
              <div
                key={p.id}
                className={`group relative rounded-3xl flex flex-col transition-all duration-300 ${
                  isPro
                    ? "md:scale-[1.04] md:-my-1 bg-gradient-to-b from-purple-950/60 via-zinc-900 to-zinc-950 border-2 border-purple-500/60 shadow-[0_0_60px_-10px_rgba(168,85,247,0.5)] hover:shadow-[0_0_80px_-5px_rgba(168,85,247,0.7)] hover:-translate-y-1"
                    : isPremium
                      ? "bg-zinc-900/60 backdrop-blur border-2 border-amber-500/40 shadow-[0_0_40px_-15px_rgba(245,158,11,0.4)] hover:border-amber-500/60 hover:-translate-y-1"
                      : "bg-zinc-900/60 backdrop-blur border border-zinc-800 hover:border-zinc-700 hover:-translate-y-1"
                }`}
                data-testid={`plan-${p.id}`}
              >
                {isPro && (
                  <>
                    {/* Animated glow border */}
                    <div
                      className="absolute -inset-px rounded-3xl bg-gradient-to-br from-purple-500/40 via-fuchsia-500/20 to-violet-500/40 blur-xl opacity-50 group-hover:opacity-90 transition-opacity pointer-events-none"
                      aria-hidden
                    />
                    {/* Top badge */}
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                      <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold text-white bg-gradient-to-r from-purple-600 to-fuchsia-600 shadow-lg shadow-purple-600/50">
                        <Sparkles className="h-3.5 w-3.5" />
                        {tx("popular")}
                      </span>
                    </div>
                  </>
                )}
                {isPremium && (
                  <div className="absolute -top-4 left-1/2 -translate-x-1/2 z-10">
                    <span className="inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-bold text-zinc-950 bg-gradient-to-r from-amber-300 to-orange-400 shadow-lg shadow-amber-500/40">
                      <Sparkles className="h-3.5 w-3.5" />
                      {p.cta?.[lang] || "Premium"}
                    </span>
                  </div>
                )}

                <div className="relative flex-1 flex flex-col p-8 lg:p-9">
                  {/* Name + subtitle */}
                  <div className="mb-7">
                    <div className={`text-xs uppercase tracking-[0.2em] font-bold ${isPro ? "text-purple-400" : isPremium ? "text-amber-400" : "text-zinc-400"}`}>
                      {p.name}
                    </div>
                    <div className="mt-1.5 text-sm text-zinc-500">{subtitle}</div>
                  </div>

                  {/* Price */}
                  <div className="mb-8">
                    <div className="flex items-baseline gap-1.5">
                      <span
                        className={`font-bold tracking-tight ${
                          isPro
                            ? "text-6xl bg-gradient-to-br from-purple-300 via-fuchsia-300 to-purple-400 bg-clip-text text-transparent"
                            : isPremium
                              ? "text-5xl bg-gradient-to-br from-amber-200 via-orange-300 to-amber-400 bg-clip-text text-transparent"
                              : "text-5xl text-white"
                        }`}
                      >
                        {monthlyDisplay}€
                      </span>
                      <span className="text-sm text-zinc-500 font-medium">{tx("perMonth")}</span>
                    </div>
                    {yearly && (
                      <div className="mt-1.5 text-xs text-zinc-500">
                        {totalYearly}€ {tx("perYear")} · {tx("monthEquiv")} {monthlyDisplay}€{tx("perMonth")}
                      </div>
                    )}
                  </div>

                  {/* CTAs — PayPal + Stripe */}
                  <div className="space-y-2 mb-8">
                    <Button
                      onClick={() => choose(p, "paypal")}
                      disabled={busyId !== null}
                      className="w-full h-12 rounded-2xl font-semibold transition-all bg-white text-zinc-900 hover:bg-zinc-100 border border-zinc-300 shadow-sm hover:shadow-md"
                      data-testid={`plan-cta-${p.id}`}
                    >
                      {busyId === `${p.id}-paypal` ? "…" : (
                        <span className="inline-flex items-center justify-center leading-none">
                          {/* Official PayPal wordmark — navy "Pa" + blue "yPal".
                              Path data from Iconify (logos:paypal). White button
                              background follows PayPal brand guidelines so the
                              logo stays clearly legible on every device. */}
                          <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 101 32"
                            className="!h-7 !w-auto !size-auto"
                            role="img"
                            aria-label="PayPal"
                          >
                            <path fill="#253B80" d="M12.237 2.8H4.437a1.085 1.085 0 0 0-1.072.916L.21 23.726a.65.65 0 0 0 .643.752h3.727a1.085 1.085 0 0 0 1.072-.916l.85-5.395a1.084 1.084 0 0 1 1.071-.917h2.469c5.136 0 8.099-2.484 8.873-7.41.349-2.155.015-3.848-.994-5.034-1.108-1.303-3.072-1.992-5.684-1.992zM13.137 10.11c-.426 2.797-2.563 2.797-4.629 2.797H7.332l.825-5.226a.65.65 0 0 1 .643-.549h.539c1.407 0 2.735 0 3.421.802.41.479.535 1.19.377 2.176z" />
                            <path fill="#253B80" d="M35.288 10.02h-3.74a.65.65 0 0 0-.643.549l-.165 1.046-.262-.379c-.81-1.175-2.616-1.568-4.42-1.568-4.135 0-7.667 3.132-8.355 7.527-.358 2.193.151 4.29 1.395 5.752 1.142 1.343 2.775 1.903 4.718 1.903 3.331 0 5.18-2.143 5.18-2.143l-.167 1.04a.65.65 0 0 0 .642.752h3.371a1.085 1.085 0 0 0 1.072-.916l2.022-12.81a.647.647 0 0 0-.641-.752m-5.211 7.286c-.361 2.139-2.061 3.575-4.229 3.575-1.088 0-1.958-.349-2.516-1.011-.554-.658-.764-1.594-.586-2.638.337-2.121 2.065-3.604 4.2-3.604 1.064 0 1.93.354 2.499 1.021.571.674.797 1.617.632 2.657" />
                            <path fill="#253B80" d="M55.21 10.02h-3.758a1.09 1.09 0 0 0-.897.476l-5.184 7.638-2.197-7.34a1.087 1.087 0 0 0-1.041-.774H38.44a.65.65 0 0 0-.617.861l4.139 12.143-3.892 5.494a.65.65 0 0 0 .53 1.027h3.755a1.08 1.08 0 0 0 .891-.465L55.74 11.039a.65.65 0 0 0-.531-1.019" />
                            <path fill="#179BD7" d="M67.643 2.8h-7.802a1.084 1.084 0 0 0-1.071.916l-3.155 20.009a.648.648 0 0 0 .64.752h4.005a.76.76 0 0 0 .75-.641l.895-5.67a1.084 1.084 0 0 1 1.071-.917h2.468c5.137 0 8.099-2.484 8.875-7.41.35-2.155.014-3.848-.995-5.034-1.108-1.303-3.07-1.992-5.681-1.992zM68.543 10.11c-.425 2.797-2.562 2.797-4.629 2.797h-1.176l.826-5.226a.648.648 0 0 1 .642-.549h.539c1.406 0 2.735 0 3.422.802.41.479.534 1.19.376 2.176" />
                            <path fill="#179BD7" d="M90.694 10.02h-3.738a.646.646 0 0 0-.642.549l-.165 1.046-.263-.379c-.81-1.175-2.615-1.568-4.419-1.568-4.135 0-7.666 3.132-8.354 7.527-.357 2.193.15 4.29 1.394 5.752 1.144 1.343 2.775 1.903 4.718 1.903 3.331 0 5.18-2.143 5.18-2.143l-.167 1.04a.65.65 0 0 0 .643.752h3.37a1.085 1.085 0 0 0 1.071-.916l2.023-12.81a.65.65 0 0 0-.651-.752m-5.211 7.286c-.359 2.139-2.06 3.575-4.228 3.575-1.087 0-1.958-.349-2.516-1.011-.554-.658-.764-1.594-.586-2.638.336-2.121 2.065-3.604 4.2-3.604 1.064 0 1.93.354 2.499 1.021.571.674.797 1.617.632 2.657" />
                            <path fill="#179BD7" d="m95.103 3.351-3.201 20.374a.648.648 0 0 0 .642.752h3.226a1.084 1.084 0 0 0 1.072-.916L99.999 3.553a.65.65 0 0 0-.642-.752H95.74a.65.65 0 0 0-.637.549" />
                          </svg>
                        </span>
                      )}
                    </Button>
                    <Button
                      onClick={() => choose(p, "stripe")}
                      disabled={busyId !== null}
                      variant="outline"
                      className="w-full h-12 rounded-2xl font-semibold transition-all border-zinc-700 bg-zinc-900/50 hover:bg-zinc-800 text-white"
                      data-testid={`plan-cta-${p.id}-stripe`}
                    >
                      {busyId === `${p.id}-stripe` ? "…" : (
                        <>
                          <svg viewBox="0 0 24 24" className="h-4 w-4 mr-2 fill-current" aria-hidden><path d="M3 5h18a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2zm0 4v8h18V9H3zm2 6h4v-1H5v1z"/></svg>
                          {lang === "de" ? "Kreditkarte" : lang === "tr" ? "Kredi Kartı" : "Credit Card"}
                        </>
                      )}
                    </Button>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3.5">
                    {features.map((f, i) => (
                      <li key={i} className="flex items-start gap-2.5 text-sm leading-relaxed text-zinc-300">
                        <span
                          className={`w-5 h-5 rounded-full grid place-items-center shrink-0 mt-0.5 ${
                            isPro ? "bg-purple-500/20 text-purple-300" : "bg-zinc-800 text-zinc-400"
                          }`}
                        >
                          <Check className="h-3 w-3" strokeWidth={3} />
                        </span>
                        <span>{f}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer note */}
        <p className="mt-16 text-center text-sm text-zinc-500" data-testid="pricing-footnote">
          {tx("footnote")}
        </p>

        {/* Demo links */}
        <div className="mt-10 text-center" data-testid="pricing-demos">
          <div className="text-xs uppercase tracking-widest text-zinc-500 font-semibold mb-4">
            {lang === "de" ? "Sieh dir Live-Demos an" : lang === "tr" ? "Canlı demoları gör" : "See live demos"}
          </div>
          <div className="flex flex-wrap gap-3 justify-center">
            <a href="/m/sultan-ocakbasi-demo" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold bg-amber-500/10 text-amber-300 hover:bg-amber-500/20 border border-amber-500/30 transition-colors">
              🥙 Sultan Ocakbaşı
            </a>
            <a href="/m/cafe-luna-demo" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold bg-stone-500/10 text-stone-300 hover:bg-stone-500/20 border border-stone-500/30 transition-colors">
              ☕ Café Luna
            </a>
            <a href="/m/ember-blade-demo" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold bg-red-500/10 text-red-300 hover:bg-red-500/20 border border-red-500/30 transition-colors">
              🥩 Ember & Blade
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
